import { useState, useEffect, useRef, useCallback } from "react";
import {
  Search, ChevronUp, ChevronDown, ChevronsUpDown,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  X, SlidersHorizontal,
} from "lucide-react";

// ── Utility: resolve dot-notation key from object ─────────────────
const get = (obj, path) => {
  if (!path) return undefined;
  return path.split(".").reduce((acc, key) => acc?.[key], obj);
};

// ── Utility: debounce hook ────────────────────────────────────────
const useDebounce = (value, delay = 300) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

// ── Sub-components ────────────────────────────────────────────────
const SortIcon = ({ dir }) => {
  if (dir === "asc")  return <ChevronUp size={14} className="text-orange-500" />;
  if (dir === "desc") return <ChevronDown size={14} className="text-orange-500" />;
  return <ChevronsUpDown size={14} className="text-gray-300" />;
};

const SkeletonRow = ({ cols }) => (
  <tr className="border-b border-gray-100">
    {Array.from({ length: cols }).map((_, i) => (
      <td key={i} className="p-4">
        <div className="h-4 bg-gray-100 rounded-full animate-pulse" style={{ width: `${60 + Math.random() * 30}%` }} />
      </td>
    ))}
  </tr>
);

const EmptyState = ({ text }) => (
  <tr>
    <td colSpan={999} className="py-16 text-center">
      <div className="flex flex-col items-center gap-3 text-gray-400">
        <SlidersHorizontal size={36} className="opacity-30" />
        <p className="font-medium text-sm">{text}</p>
      </div>
    </td>
  </tr>
);

// ── Pagination controls ───────────────────────────────────────────
const Pagination = ({ page, totalPages, onChange }) => {
  const pages = [];

  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (page > 3) pages.push("...");
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
      pages.push(i);
    }
    if (page < totalPages - 2) pages.push("...");
    pages.push(totalPages);
  }

  const btn = (content, target, disabled) => (
    <button
      key={typeof content === "string" ? content + target : content}
      onClick={() => !disabled && onChange(target)}
      disabled={disabled}
      className={`min-w-[32px] h-8 px-2 rounded-lg text-sm font-medium transition
        ${disabled ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-100"}
        ${content === page ? "bg-orange-500 text-white hover:bg-orange-500" : "text-gray-600"}
      `}
    >
      {content}
    </button>
  );

  return (
    <div className="flex items-center gap-1">
      {btn(<ChevronsLeft size={14} />, 1, page === 1)}
      {btn(<ChevronLeft size={14} />, page - 1, page === 1)}
      {pages.map((p, i) =>
        p === "..." ? (
          <span key={`ellipsis-${i}`} className="px-1 text-gray-400 text-sm">…</span>
        ) : (
          btn(p, p, false)
        )
      )}
      {btn(<ChevronRight size={14} />, page + 1, page === totalPages)}
      {btn(<ChevronsRight size={14} />, totalPages, page === totalPages)}
    </div>
  );
};

// ── Main DataTable component ──────────────────────────────────────
/**
 * @param {Object[]}  columns           - Column definitions
 * @param {string}    columns[].key     - Dot-notation data key, e.g. "category.name"
 * @param {string}    columns[].label   - Header label
 * @param {boolean}   [columns[].sortable] - Enable sort on this column
 * @param {Function}  [columns[].render]   - Custom render: (value, row) => ReactNode
 * @param {string}    [columns[].width]    - Tailwind width class e.g. "w-48"
 * @param {Object[]}  data              - Full dataset (filtering/sort/page done internally)
 * @param {boolean}   [loading]         - Show skeleton rows
 * @param {string[]}  [searchKeys]      - Keys to search against (dot-notation)
 * @param {string}    [searchPlaceholder]
 * @param {Object[]}  [filters]         - Dropdown filters: { key, label, options: [{label,value}] }
 * @param {number}    [defaultPageSize] - Default rows per page (default 10)
 * @param {number[]}  [pageSizeOptions] - Page size choices (default [10,25,50])
 * @param {Function}  [actions]         - Row actions slot: (row) => ReactNode
 * @param {string}    [emptyText]       - Empty state message
 * @param {Function}  [onRowClick]      - Optional row click handler: (row) => void
 * @param {ReactNode} [headerActions]   - Extra buttons rendered next to search bar
 */
const DataTable = ({
  columns = [],
  data = [],
  loading = false,
  searchKeys = [],
  searchPlaceholder = "Search...",
  filters = [],
  defaultPageSize = 10,
  pageSizeOptions = [10, 25, 50],
  actions,
  emptyText = "No results found.",
  onRowClick,
  headerActions,
}) => {
  const [rawSearch, setRawSearch] = useState("");
  const [sort, setSort]           = useState({ key: null, dir: "asc" });
  const [activeFilters, setActiveFilters] = useState({});
  const [page, setPage]           = useState(1);
  const [pageSize, setPageSize]   = useState(defaultPageSize);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const search = useDebounce(rawSearch);

  // Reset to page 1 whenever search/filters change
  useEffect(() => { setPage(1); }, [search, activeFilters, sort]);

  // ── 1. Search ──────────────────────────────────────────────────
  const searched = search
    ? data.filter((row) =>
        searchKeys.some((k) => {
          const val = get(row, k);
          return String(val ?? "").toLowerCase().includes(search.toLowerCase());
        })
      )
    : data;

  // ── 2. Filter ──────────────────────────────────────────────────
  const filtered = Object.entries(activeFilters).reduce((rows, [key, val]) => {
    if (!val) return rows;
    return rows.filter((row) => String(get(row, key)) === String(val));
  }, searched);

  // ── 3. Sort ────────────────────────────────────────────────────
  const sorted = sort.key
    ? [...filtered].sort((a, b) => {
        const av = get(a, sort.key) ?? "";
        const bv = get(b, sort.key) ?? "";
        const cmp = typeof av === "number"
          ? av - bv
          : String(av).localeCompare(String(bv));
        return sort.dir === "asc" ? cmp : -cmp;
      })
    : filtered;

  // ── 4. Paginate ────────────────────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage   = Math.min(page, totalPages);
  const start      = (safePage - 1) * pageSize;
  const paginated  = sorted.slice(start, start + pageSize);

  const handleSort = (key) => {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" }
    );
  };

  const setFilter = (key, val) => {
    setActiveFilters((prev) => ({ ...prev, [key]: val }));
  };

  const clearFilters = () => {
    setActiveFilters({});
    setRawSearch("");
  };

  const activeFilterCount = Object.values(activeFilters).filter(Boolean).length;
  const hasActiveFilters  = activeFilterCount > 0 || rawSearch;

  const allCols = actions
    ? [...columns, { key: "__actions", label: "Actions", _actions: true }]
    : columns;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">

      {/* ── Toolbar ───────────────────────────────────────────── */}
      <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">

        {/* Left: search */}
        <div className="flex items-center gap-3 w-full sm:w-auto">
          {searchKeys.length > 0 && (
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
              <input
                value={rawSearch}
                onChange={(e) => setRawSearch(e.target.value)}
                placeholder={searchPlaceholder}
                className="pl-9 pr-4 py-2 w-full border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
              />
              {rawSearch && (
                <button onClick={() => setRawSearch("")} className="absolute right-2.5 top-2.5 text-gray-400 hover:text-gray-600">
                  <X size={14} />
                </button>
              )}
            </div>
          )}

          {/* Filter toggle */}
          {filters.length > 0 && (
            <button
              onClick={() => setFiltersOpen((v) => !v)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-sm font-medium transition ${
                filtersOpen || activeFilterCount > 0
                  ? "border-orange-400 bg-orange-50 text-orange-600"
                  : "border-gray-200 text-gray-600 hover:bg-gray-50"
              }`}
            >
              <SlidersHorizontal size={15} />
              Filters
              {activeFilterCount > 0 && (
                <span className="bg-orange-500 text-white text-xs w-4 h-4 rounded-full flex items-center justify-center font-bold">
                  {activeFilterCount}
                </span>
              )}
            </button>
          )}

          {/* Clear all */}
          {hasActiveFilters && (
            <button onClick={clearFilters} className="text-xs text-gray-400 hover:text-red-500 transition flex items-center gap-1">
              <X size={12} /> Clear
            </button>
          )}
        </div>

        {/* Right: result count + extra actions */}
        <div className="flex items-center gap-3">
          {!loading && (
            <span className="text-xs text-gray-400 whitespace-nowrap">
              {filtered.length === data.length
                ? `${data.length} rows`
                : `${filtered.length} of ${data.length}`}
            </span>
          )}
          {headerActions}
        </div>
      </div>

      {/* ── Filter dropdowns ──────────────────────────────────── */}
      {filtersOpen && filters.length > 0 && (
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex flex-wrap gap-3">
          {filters.map((f) => (
            <div key={f.key} className="flex flex-col gap-1 min-w-[140px]">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">{f.label}</label>
              <select
                value={activeFilters[f.key] || ""}
                onChange={(e) => setFilter(f.key, e.target.value)}
                className="border border-gray-200 rounded-xl px-3 py-1.5 text-sm bg-white outline-none focus:ring-2 focus:ring-orange-500 transition"
              >
                <option value="">All</option>
                {f.options.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          ))}
        </div>
      )}

      {/* ── Table ─────────────────────────────────────────────── */}
      <div className="overflow-x-auto">
        <table className="w-full text-left whitespace-nowrap">
          <thead className="bg-gray-50 text-gray-500 text-xs uppercase font-bold tracking-wider border-b border-gray-100">
            <tr>
              {allCols.map((col) => (
                <th
                  key={col.key}
                  className={`px-4 py-3 ${col.sortable ? "cursor-pointer select-none hover:text-gray-800 transition" : ""} ${col.width ?? ""}`}
                  onClick={() => col.sortable && handleSort(col.key)}
                >
                  <div className={`flex items-center gap-1 ${col._actions ? "justify-end" : ""}`}>
                    {col.label}
                    {col.sortable && <SortIcon dir={sort.key === col.key ? sort.dir : null} />}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading
              ? Array.from({ length: pageSize }).map((_, i) => (
                  <SkeletonRow key={i} cols={allCols.length} />
                ))
              : paginated.length === 0
              ? <EmptyState text={emptyText} />
              : paginated.map((row, ri) => (
                  <tr
                    key={row.id ?? ri}
                    onClick={() => onRowClick?.(row)}
                    className={`hover:bg-gray-50 transition ${onRowClick ? "cursor-pointer" : ""}`}
                  >
                    {columns.map((col) => (
                      <td key={col.key} className={`px-4 py-3 text-sm ${col.width ?? ""}`}>
                        {col.render
                          ? col.render(get(row, col.key), row)
                          : <span className="text-gray-700">{String(get(row, col.key) ?? "—")}</span>
                        }
                      </td>
                    ))}
                    {actions && (
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {actions(row)}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
            }
          </tbody>
        </table>
      </div>

      {/* ── Footer: page size + pagination ────────────────────── */}
      {!loading && sorted.length > 0 && (
        <div className="px-4 py-3 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>Rows per page</span>
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
              className="border border-gray-200 rounded-lg px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-orange-500 transition"
            >
              {pageSizeOptions.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <span className="hidden sm:inline text-gray-400">
              {start + 1}–{Math.min(start + pageSize, sorted.length)} of {sorted.length}
            </span>
          </div>
          <Pagination page={safePage} totalPages={totalPages} onChange={setPage} />
        </div>
      )}
    </div>
  );
};

export default DataTable;
